import {
  Component,
  SimpleChanges,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { QuotaPurePrice, Range, RangeList } from './pure-price.interface';
import {
  AccordionDirective,
  SectionToggleInfo,
} from '@purespectrum1/ui/accordion';
import { PurePriceService } from '../../shared/services/pure-price/pure-price.service';
import { isEqual } from '../utils/deep-compare';
import { SurveyMetaDataService } from '../../shared/services/survey-meta-data/survey-meta-data.service';
import { Subscription } from 'rxjs';
import {
  CPIData,
  SurveyTopBarData,
} from '../../shared/interfaces/survey-meta-data.interface';
import { FlexibilityPricesParams } from '../../shared/interfaces/supplier-api.interface';
import {
  MetaDataToPurePrice,
  Permutation,
} from '../../shared/interfaces/black-box.interface';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { BuyerSurveyResponse } from '../../shared/interfaces/survey.interface';
import { UserInteractionStatsQualPayload } from '../../shared/interfaces/quota.interface';
import { AuthService } from '../../shared/services/auth/auth.service';

@Component({
  selector: 'ps-ui-pure-price-window',
  templateUrl: './pure-price-window.component.html',
  styleUrls: ['./pure-price-window.component.css'],
})
export class PurePriceWindowComponent implements OnInit, OnChanges {
  @Input() public purePriceData!: MetaDataToPurePrice;
  @Input() public surveyDetails!: BuyerSurveyResponse;
  @Input() appliedQuotaName?: string;

  public quotasPurePrice: QuotaPurePrice[] = [];
  public showPurePriceWindow: boolean = false;
  public surveyId!: number;
  public selectedQualificationCode: string = '';
  public loading: boolean = false;
  public averageCpi: RangeList = {} as RangeList;
  public fieldTime: Range = {} as Range;
  public showQuotaDetails: boolean = false;
  @Output() purePriceApplied = new EventEmitter<QuotaPurePrice>();
  @Output() purePriceFieldTimeApplied = new EventEmitter<number>();
  @Output() appliedQuotaNameChange = new EventEmitter();
  @ViewChild(AccordionDirective) accordion!: AccordionDirective;
  private _subscriptions: Subscription = new Subscription();
  private _cpiData: CPIData = {} as CPIData;
  public blockSelection: boolean = false;
  private _surveyTopBarData = {} as SurveyTopBarData;
  private _applyButtonWasSeen: boolean = false;
  private _appliedQuotas?: QuotaPurePrice;
  public isPurePriceApplied: boolean = false;
  public isPurePriceRateCardEnable: boolean = false;
  private _permutations: Permutation[] = [];

  constructor(
    private _auth: AuthService,
    private _toastrService: ToasterService,
    private _purePriceService: PurePriceService,
    private _surveyMetaDataService: SurveyMetaDataService
  ) {}

  ngOnInit() {
    this._setUpFlags();
    if (!this.isPurePriceRateCardEnable) {
      this._getQuotas();
    } else {
      this.loading = true;
    }

    this.openPurePriceWindow();
    this._subscriptions.add(
      this._surveyMetaDataService.getCPIData().subscribe((data: CPIData) => {
        this._cpiData = data;
      })
    );
    this._subscriptions.add(
      this._surveyMetaDataService
        .getSurveyHeaderData()
        .subscribe((topBarDataReceived) => {
          this._surveyTopBarData = topBarDataReceived;
        })
    );
    this._subscriptions.add(
      this._surveyMetaDataService
        .getFeasibilityData()
        .subscribe((feasibility) => {
          if (this.isPurePriceRateCardEnable) {
            this._permutations =
              feasibility.response?.field_time_slider_values || [];
            if (this._permutations.length) {
              this.loading = false;
              this._getPermutationsPricingRecoV2();
            }
          }
        })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this._isSurveyDataUpdate(changes)) {
      if (!this.isPurePriceRateCardEnable) {
        this._getQuotas();
      }
    } else if (!this._auth.buyerConfig?.enableAdvanceFeasibilityDropDown) {
      // We have stopped calling feasibility API if no changes are made to metadata in case of Advance feasibility.
      // therefore we should not enable loading in such scenario when no data is changed. It should use the existing data.
      // if data is changed then feasibility will be called and loader will be closed once we receive data from `getFeasibilityData()`
      this.loading = true;
    }
  }

  private _setUpFlags() {
    this.surveyId = this.surveyDetails.ps_survey_id;
    this.isPurePriceApplied = this.surveyDetails.price_reco;
    this.isPurePriceRateCardEnable =
      this.purePriceData.survey.is_pure_price_rate_card_enable;
  }

  private _isSurveyDataUpdate(changes: SimpleChanges) {
    return !!(
      changes.purePriceData &&
      !changes.purePriceData.firstChange &&
      !isEqual(
        changes.purePriceData.currentValue,
        changes.purePriceData.previousValue
      )
    );
  }

  private _getQuotas() {
    if (
      !this.purePriceData.survey ||
      !this.purePriceData.quotas ||
      this.loading
    ) {
      return;
    }
    this.loading = true;
    this._purePriceService
      .getQuotas(this.purePriceData.survey, this.purePriceData.quotas)
      .subscribe((quotas: QuotaPurePrice[]) => {
        this.loading = false;
        this.quotasPurePrice = quotas;
        if (quotas.length) {
          const selectedQuota =
            quotas.find(
              (quota) =>
                String(quota.name).toLowerCase() ===
                String(this.appliedQuotaName).toLowerCase()
            ) || quotas[0];
          this.selectedQualificationCode = selectedQuota.code;
          this._setRangeBounds();
        }
      });
  }

  private _getSelectedQuota() {
    return this.quotasPurePrice.find(
      (q) => q.code === this.selectedQualificationCode
    );
  }

  private _setRangeBounds() {
    const quota = this._getSelectedQuota();
    if (!quota) {
      return;
    }
    const cpis = Array.apply(null, Array(30)).map((x) => 0);
    const fieldTimes = quota.permutations.map((p, i) => {
      cpis[cpis.length - 1 - i] = p.sup_cpi;
      return p.field_time;
    });
    //const cpis = quota.permutations.map((p) => p.sup_cpi);
    this.fieldTime.min = Math.min(...fieldTimes);
    this.fieldTime.max = Math.max(...fieldTimes);
    // sets are always unique
    this.averageCpi.steps = cpis;
    // set values from survey
    this.fieldTime.value = this.purePriceData.survey.field_time;
    this.adjustRanges(this.fieldTime.value, false);
    if (this.purePriceData.purePriceApplied) {
      const quotaApplied = this._getSelectedQuota();
      this.blockSelection = true;
      this.appliedQuotaName = quotaApplied!.name;
    }
  }

  public openPurePriceWindow(isManualClose = false) {
    if (!isManualClose) {
      this.showPurePriceWindow = true;
      this._purePriceService.setPricingRecoFlyoutOpened(Number(this.surveyId));
    } else {
      this.showPurePriceWindow = false;
    }
  }

  public applyPurePrice() {
    if (!this.isPurePriceRateCardEnable) {
      if (this.appliedQuotaName) {
        // Removing Price Reco
        this.isPurePriceApplied = false;
        this.blockSelection = false;
        this.appliedQuotaName = undefined;
        this._toastrService.warning(
          'You removed PurePrice™. However, the changes you may have made to Field time have already been applied to this project, and the survey has had QBP enabled. Please verify that they represent the settings you want on your project'
        );
        this.purePriceApplied.emit(undefined);
        this._cpiData.suppliers?.forEach((supplier) => {
          supplier.cpi = supplier.block_price;
        });
        this._cpiData.flexibilityPrice = {} as FlexibilityPricesParams;
        this.appliedQuotaNameChange.emit(undefined);
      } else {
        // Apply Price Reco
        this.isPurePriceApplied = true;
        this.blockSelection = true;
        const quota = this._getSelectedQuota();
        this._appliedQuotas = quota;
        this.appliedQuotaName = quota?.name;
        this.appliedQuotaNameChange.emit(quota?.name);
        this._applyPurePriceToSelectedQuota(this.quotasPurePrice); //Remove this from parameters
        this._setUserInteraction();
      }
    } else {
      this.purePriceFieldTimeApplied.emit(this.fieldTime.value);
    }
    this._surveyMetaDataService.sendCPIData(this._cpiData);
    this._surveyTopBarData.surveyFieldTime = this.fieldTime.value;
    this._surveyMetaDataService.sendSurveyHeaderData(this._surveyTopBarData);
  }

  private _setUserInteraction(): void {
    const { expected_loi, expected_ir, ps_survey_id } = this.surveyDetails;
    const { name, criterias } = this._appliedQuotas!;
    const purePriceQuotaStats = criterias.map((criteria) => {
      return <UserInteractionStatsQualPayload>{
        loss_ratio: criteria.loss_ratio,
        q_name: criteria.description,
        price: criteria.price,
        EPC: criteria.EPC,
      };
    });
    this._purePriceService.setUserInteraction(
      ps_survey_id,
      expected_loi,
      expected_ir,
      name,
      purePriceQuotaStats
    );
  }

  private _applyPurePriceToSelectedQuota(quotaPurePrice: QuotaPurePrice[]) {
    const selectedDayIndex = this.fieldTime.value - 1;
    const purePriceQuotaSelected = quotaPurePrice.find(
      (selectedQuota) => selectedQuota.name === this.appliedQuotaName
    );

    purePriceQuotaSelected!.criterias = purePriceQuotaSelected!.criterias.map(
      (purePriceQuota) => ({
        ...purePriceQuota,
        priceRecommended:
          purePriceQuota.permutations[selectedDayIndex]?.sup_cpi,
      })
    );

    if (purePriceQuotaSelected) {
      this.purePriceApplied.emit({
        ...purePriceQuotaSelected,
        fieldTime: this.fieldTime.value,
      });
      const averageCpi =
        purePriceQuotaSelected.permutations[this.fieldTime.value - 1]?.sup_cpi;
      this._cpiData.suppliers?.forEach((supplier) => {
        supplier.cpi = averageCpi;
      });
      const flexibilityPrice = {} as FlexibilityPricesParams;
      flexibilityPrice.survey_price_override = averageCpi;
      flexibilityPrice.manual_override = true;
      this._cpiData.flexibilityPrice = flexibilityPrice;
    }
    this._purePriceService.pushApplyPriceRecoButtonCount(
      this.surveyDetails.ps_survey_id
    );
  }

  public selectQuota(quota: QuotaPurePrice) {
    this.appliedQuotaName = undefined;
    this.selectedQualificationCode = quota.code;
    this._setRangeBounds();
    this.accordion.sections.first.close();
  }

  public quotaDetailsToggle(info: SectionToggleInfo) {
    this.showQuotaDetails = info.opened;
  }

  public openDetails(): void {
    if (!this._applyButtonWasSeen && this.surveyDetails.ps_survey_id) {
      this._purePriceService.pushApplyPriceRecoButtonSeenCount(
        this.surveyDetails.ps_survey_id
      );
      this._applyButtonWasSeen = true;
    }
  }

  public adjustRanges(value: number, isCpi: boolean) {
    const quota = this._getSelectedQuota();
    const permutations = this.isPurePriceRateCardEnable
      ? this._permutations
      : quota!.permutations;
    const permutation = permutations.find((p, index) =>
      isCpi ? permutations.length - 1 - index === value : p.field_time === value
    );
    const indexToSet = permutations.findIndex(
      (p, index) => permutations.length - index === value
    );
    if (isCpi) {
      this.fieldTime.value = permutation!.field_time;
    } else {
      this.averageCpi.index = indexToSet;
    }
  }

  public daysFormatter(value: number) {
    return `${value} Day${value > 1 ? 's' : ''}`;
  }

  private _getPermutationsPricingRecoV2() {
    if (!this._permutations.length) {
      return;
    }
    const cpis = Array.apply(null, Array(20)).map((x) => 0);
    const fieldTimes = this._permutations.map((p, i) => {
      cpis[cpis.length - 1 - i] = p.sup_cpi;
      return p.field_time;
    });
    //const cpis = quota.permutations.map((p) => p.sup_cpi);
    this.fieldTime.min = Math.min(...fieldTimes);
    this.fieldTime.max = Math.max(...fieldTimes);
    // sets are always unique
    this.averageCpi.steps = cpis;
    // set values from survey
    this.fieldTime.value = this._surveyTopBarData.surveyFieldTime;
    this.adjustRanges(this.fieldTime.value, false);
  }
}
